import type { ModoWhatsapp, ValoresAjustes } from '../../api/modelos'

/** Estado editable de Ajustes (RF-47). Listas de correo como texto, una por línea o con comas. */
export interface FormAjustes {
  whatsapp_modo: ModoWhatsapp
  whatsapp_prefijo_pais: string
  correo_activo: boolean
  pedidos: string
  rutas: string
  incidencias: string
  geocodificacion_activa: boolean
  geocodificacion_url: string
  geocodificacion_region: string
  salida_latitud: string
  salida_longitud: string
  salida_etiqueta: string
  rutas_por_dia: string
  pedido_producto_envase: string
  pedido_producto_devolucion: string
}

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function listaCorreos(texto: string): string[] {
  return [...new Set(texto.split(/[\s,;]+/).map((c) => c.trim()).filter(Boolean))]
}

export function formDeValores(v: ValoresAjustes): FormAjustes {
  const d = v.correo_destinatarios ?? { pedidos: [], rutas: [], incidencias: [] }
  return {
    whatsapp_modo: v.whatsapp_modo ?? 'manual',
    whatsapp_prefijo_pais: String(v.whatsapp_prefijo_pais ?? '34'),
    correo_activo: Boolean(v.correo_activo),
    pedidos: (d.pedidos ?? []).join('\n'),
    rutas: (d.rutas ?? []).join('\n'),
    incidencias: (d.incidencias ?? []).join('\n'),
    geocodificacion_activa: Boolean(v.geocodificacion_activa),
    geocodificacion_url: v.geocodificacion_url ?? 'https://nominatim.openstreetmap.org/search',
    geocodificacion_region: v.geocodificacion_region ?? '',
    salida_latitud: v.ruta_salida ? String(v.ruta_salida.latitud) : '',
    salida_longitud: v.ruta_salida ? String(v.ruta_salida.longitud) : '',
    salida_etiqueta: v.ruta_salida?.etiqueta ?? '',
    rutas_por_dia: String(v.rutas_por_dia ?? 2),
    pedido_producto_envase: v.pedido_producto_envase ?? 'ENVASE',
    pedido_producto_devolucion: v.pedido_producto_devolucion ?? 'DEVOLUCION ENVASE',
  }
}

function num(texto: string): number {
  return Number(texto.trim().replace(',', '.'))
}

export function validarAjustes(f: FormAjustes): string[] {
  const errores: string[] = []
  if (!/^\d{1,4}$/.test(f.whatsapp_prefijo_pais.trim())) errores.push('El prefijo de país son de 1 a 4 dígitos, sin «+».')
  for (const [campo, nombre] of [['pedidos', 'pedidos'], ['rutas', 'rutas'], ['incidencias', 'incidencias']] as const) {
    const malos = listaCorreos(f[campo]).filter((c) => !RE_EMAIL.test(c))
    if (malos.length) errores.push(`Correo no válido en ${nombre}: ${malos.join(', ')}`)
  }
  const n = num(f.rutas_por_dia)
  if (!Number.isInteger(n) || n < 1 || n > 10) errores.push('Las rutas por día tienen que estar entre 1 y 10.')
  const conSalida = f.salida_latitud.trim() !== '' || f.salida_longitud.trim() !== ''
  if (conSalida) {
    const lat = num(f.salida_latitud)
    const lng = num(f.salida_longitud)
    if (!Number.isFinite(lat) || lat < -90 || lat > 90 || f.salida_latitud.trim() === '') errores.push('La latitud del punto de salida tiene que estar entre -90 y 90.')
    if (!Number.isFinite(lng) || lng < -180 || lng > 180 || f.salida_longitud.trim() === '') errores.push('La longitud del punto de salida tiene que estar entre -180 y 180.')
  }
  if (f.geocodificacion_activa && !/^https?:\/\//.test(f.geocodificacion_url.trim())) errores.push('La URL de geocodificación tiene que empezar por http:// o https://.')
  return errores
}

export function valoresDeForm(f: FormAjustes): ValoresAjustes {
  const conSalida = f.salida_latitud.trim() !== '' && f.salida_longitud.trim() !== ''
  return {
    whatsapp_modo: f.whatsapp_modo,
    whatsapp_prefijo_pais: f.whatsapp_prefijo_pais.trim(),
    correo_activo: f.correo_activo,
    correo_destinatarios: { pedidos: listaCorreos(f.pedidos), rutas: listaCorreos(f.rutas), incidencias: listaCorreos(f.incidencias) },
    geocodificacion_activa: f.geocodificacion_activa,
    geocodificacion_url: f.geocodificacion_url.trim(),
    geocodificacion_region: f.geocodificacion_region.trim(),
    ruta_salida: conSalida ? { latitud: num(f.salida_latitud), longitud: num(f.salida_longitud), etiqueta: f.salida_etiqueta.trim() } : null,
    rutas_por_dia: num(f.rutas_por_dia),
    pedido_producto_envase: f.pedido_producto_envase.trim(),
    pedido_producto_devolucion: f.pedido_producto_devolucion.trim(),
  }
}

/** Solo las claves que han cambiado: el backend ignora o rechaza el resto. */
export function cambios(antes: ValoresAjustes, ahora: ValoresAjustes): ValoresAjustes {
  const salida: ValoresAjustes = {}
  for (const [clave, valor] of Object.entries(ahora)) {
    if (JSON.stringify(valor) !== JSON.stringify(antes[clave])) salida[clave] = valor
  }
  return salida
}
